import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { mergeMap, Observable, tap, throwError } from "rxjs";
import { BeginTotpSetupDto, MfaSettingsDto } from "../models/mfa.models";
import { MfaService } from "../services/mfa.service";
import { MfaActions } from "./mfa.actions";

export interface MfaStateModel {
    settings: MfaSettingsDto | null;
    setup: BeginTotpSetupDto | null;
}

@State<MfaStateModel>({
    name: "mfa",
    defaults: {
        settings: null,
        setup: null,
    },
}) @Injectable()
export class MfaState {
    public constructor(private readonly mfaService: MfaService) {}

    @Selector()
    public static settings(state: MfaStateModel): MfaSettingsDto | null {
        return state?.settings;
    }

    @Selector()
    public static setup(state: MfaStateModel): BeginTotpSetupDto | null {
        return state?.setup;
    }

    @Selector()
    public static isEnabled(state: MfaStateModel): boolean {
        return state?.settings?.isEnabled ?? false;
    }

    @Selector()
    public static requiresSetup(state: MfaStateModel): boolean {
        return state?.settings?.mustEnableTwoFactor === true && !state?.settings?.isEnabled;
    }

    @Action(MfaActions.GetSettings)
    public getSettings(ctx: StateContext<MfaStateModel>): Observable<MfaSettingsDto> {
        return this.mfaService.getSettings().pipe(tap((settings) => {
            ctx.patchState({ settings });
        }));
    }

    @Action(MfaActions.BeginSetup)
    public beginSetup(ctx: StateContext<MfaStateModel>): Observable<BeginTotpSetupDto> {
        return this.mfaService.beginSetup().pipe(tap((setup) => {
            ctx.patchState({ setup });
        }));
    }

    @Action(MfaActions.BeginSetupForUser)
    public beginSetupForUser(
        ctx: StateContext<MfaStateModel>,
        action: MfaActions.BeginSetupForUser,
    ): Observable<BeginTotpSetupDto> {
        return this.mfaService.beginSetupForUser(action.userId).pipe(tap((setup) => {
            ctx.patchState({ setup });
        }));
    }

    @Action(MfaActions.ConfirmSetup)
    public confirmSetup(ctx: StateContext<MfaStateModel>, action: MfaActions.ConfirmSetup): Observable<unknown> {
        return this.mfaService.confirmSetup(action.code).pipe(mergeMap((result) => {
            if (result.success) {
                return [result];
            } else {
                // Confirmation failed - throw error with the error code
                return throwError(() => ({
                    errorCode: result.errorCode || "UNKNOWN",
                    message: "MFA confirmation failed",
                }));
            }
        }));
    }

    @Action(MfaActions.Disable)
    public disable(ctx: StateContext<MfaStateModel>, action: MfaActions.Disable): Observable<unknown> {
        return this.mfaService.verifyMfaCode(action.userId, action.code).pipe(mergeMap((result) => {
            if (result.success) {
                return this.mfaService.disable().pipe(tap(() => {
                    ctx.dispatch(new MfaActions.GetSettings());
                }));
            } else {
                // Verification failed - throw error with the error code
                return throwError(() => ({
                    errorCode: result.errorCode || "UNKNOWN",
                    message: "MFA verification failed",
                }));
            }
        }));
    }

    @Action(MfaActions.CheckRequiresSetup)
    public checkRequiresSetup(ctx: StateContext<MfaStateModel>): void {
        ctx.dispatch(new MfaActions.GetSettings());
    }
}
